import React, { useMemo, Suspense } from "react";
import { useTranslation } from "../../../contexts/I18nProvider";
import { useTheme } from "../../../contexts/ThemeContext";
import styles from "./ProfileContent.module.css";

// Lazy load sections for better performance
const OverviewSection = React.lazy(() => import("./sections/OverviewSection"));
const AccountInformationView = React.lazy(() => import("./sections/AccountInformationSection/AccountInformationView"));
const SubscriptionsSection = React.lazy(() => import("./sections/SubscriptionsSection/SubscriptionsSection"));

// Loading fallback component
const SectionLoader = () => (
  <div className={styles.section} style={{ textAlign: 'center', padding: '2rem' }}>
    <p>Loading...</p>
  </div>
);

const ProfileContent = React.memo(({ activeSection }) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();

  // Memoize the section content to prevent unnecessary re-renders
  const sectionContent = useMemo(() => {
    switch (activeSection) {
      case "overview":
        return (
          <Suspense fallback={<SectionLoader />}>
            <OverviewSection />
          </Suspense>
        );

      case "account":
        return (
          <Suspense fallback={<SectionLoader />}>
            <AccountInformationView />
          </Suspense>
        );

      case "subscription":
        return (
          <Suspense fallback={<SectionLoader />}>
            <SubscriptionsSection />
          </Suspense>
        );

      case "preferences":
        return (
          <div className={styles.section}>
            <h2 className={styles.title}>{t("pages.profile.preferences")}</h2>
            {/* Preferences section content will be added in Sprint 2 */}
          </div>
        );

      case "security":
        return (
          <div className={styles.section}>
            <h2 className={styles.title}>{t("pages.profile.security")}</h2>
            {/* Security section content will be added in Sprint 2 */}
          </div>
        );

      case "help":
        return (
          <div className={styles.section}>
            <h2 className={styles.title}>{t("pages.profile.help")}</h2>
            {/* Help section content will be added in Sprint 2 */}
          </div>
        );

      default:
        return null;
    }
  }, [activeSection, t]);

  return (
    <div className={`${styles.content} ${isDark ? styles.dark : ""}`}>
      {/* Dynamic Section Content */}
      <div className={styles.sectionWrapper}>{sectionContent}</div>
    </div>
  );
});

ProfileContent.displayName = 'ProfileContent';

export default ProfileContent;
